import { context, trace } from '@opentelemetry/api';
import { logger } from '@parrot-software/observability';
import { zipSync } from 'fflate';
import { toast } from 'sonner';
import { extractErrorAttributes } from '@subscriptions/application/utils';
import { fetchAsBytes } from './fetchAsBytes';

const DEFAULT_BASE_NAME = 'factura';
const DEFAULT_ERROR_MESSAGE = 'No se pudo descargar la factura. Intenta de nuevo.';

const historyLog = logger.forDomain('SUB', { subdomain: 'billing' });
const tracer = trace.getTracer('@parrot-software/observability');

type DownloadTarget = { url: string; name: string };

/**
 * Triggers a browser download for a Blob via a temporary anchor element.
 * Revokes the object URL after the click to free memory.
 */
const triggerBlobDownload = (blob: Blob, fileName: string): void => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};

/**
 * Triggers a direct browser download via a temporary anchor pointing at a remote URL.
 * Used when only one file is available (no ZIP needed).
 */
const triggerDirectDownload = (url: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Downloads the PDF and/or XML invoice files for a payment.
 *
 * Behavior:
 * - When both URLs are present, the files are fetched in parallel, packed into
 *   a single ZIP named `${folio}.zip` (containing `${folio}.pdf` and `${folio}.xml`),
 *   and downloaded as a single archive. A success toast is shown only in this case.
 * - When only one URL is present, that file is downloaded directly without packaging.
 * - When both URLs are null, nothing happens.
 * - On any fetch/zip failure, a toast error is displayed and the download is aborted.
 *
 * @param pdfUrl - URL of the PDF invoice, or null when not available
 * @param xmlUrl - URL of the XML invoice, or null when not available
 * @param folio - Invoice folio used to name the ZIP and inner files (falls back to "factura" when empty)
 * @param messages - Optional copy overrides for the error and ZIP success toasts
 */
export const handleDownload = async (
  pdfUrl: string | null,
  xmlUrl: string | null,
  folio: string,
  messages?: {
    errorMessage?: string;
    successMessage?: string;
    successDescription?: string;
  },
): Promise<void> => {
  const errorMessage = messages?.errorMessage ?? DEFAULT_ERROR_MESSAGE;
  const baseName = folio.trim() || DEFAULT_BASE_NAME;
  const targets: DownloadTarget[] = [
    pdfUrl ? { url: pdfUrl, name: `${baseName}.pdf` } : null,
    xmlUrl ? { url: xmlUrl, name: `${baseName}.xml` } : null,
  ].filter((target): target is DownloadTarget => target !== null);

  if (targets.length === 0) return;

  const mode: 'zip' | 'direct' = targets.length === 1 ? 'direct' : 'zip';

  const span = tracer.startSpan('SUB.paymentDownload', {
    attributes: {
      'payment.folio': baseName,
      'download.mode': mode,
      'download.fileCount': targets.length,
    },
  });
  const ctx = trace.setSpan(context.active(), span);

  return context.with(ctx, async () => {
    historyLog.info('startPaymentDownload', {
      'payment.folio': baseName,
      'download.mode': mode,
      'download.fileCount': targets.length,
    });

    if (mode === 'direct') {
      triggerDirectDownload(targets[0].url, targets[0].name);
      span.end();
      return;
    }

    try {
      const buffers = await Promise.all(targets.map((target) => fetchAsBytes(target.url)));
      const filesForZip = targets.reduce<Record<string, Uint8Array>>((accumulator, target, index) => {
        accumulator[target.name] = buffers[index];
        return accumulator;
      }, {});
      const zipped = zipSync(filesForZip);
      const zipBlob = new Blob([zipped], { type: 'application/zip' });
      triggerBlobDownload(zipBlob, `${baseName}.zip`);

      historyLog.info('completePaymentDownload', {
        'payment.folio': baseName,
        'download.mode': mode,
        'download.totalBytes': zipBlob.size,
      });

      if (messages?.successMessage) {
        toast.success(messages.successMessage, { description: messages.successDescription });
      }
    } catch (error) {
      historyLog.error('downloadPayment', {
        ...extractErrorAttributes(error),
        'payment.folio': baseName,
        'download.fileCount': targets.length,
      });
      toast.error(errorMessage);
    } finally {
      span.end();
    }
  });
};
